
import React from 'react';
import { DutyMarks } from '../StaffDuties';
import { CoverageGap, vacancyGaps, gapsLabel, allocateToVacancies } from '../../utils/coverage';
import { sMin as startMinOf, eMin as endMinOf, clockLabel } from '../../utils/shiftTime';
import { Users, Calendar, CalendarOff, Trash2, Plus, Minus, Lock } from 'lucide-react';
import { Duty, Shift, Position, Staff, getJSTDateString, timeToMinutes, minutesToTime, DailyEvent, api } from '../../api';

// 同じポジションで時間が重なる募集枠（例：朝食 6〜11時 2名＋混む 7〜10時 1名）は、重ならないよう段を分けて表示する
const LANE_H = 72;
const laneLayout = (list: Shift[]) => {
  const lanes: number[] = []; const map = new Map<string, number>();
  [...list].sort((a, b) => a.start.localeCompare(b.start) || b.end.localeCompare(a.end)).forEach(v => {
    const s0 = startMinOf(v); const e0 = endMinOf(v);
    let lane = lanes.findIndex(end => end <= s0);
    if (lane === -1) { lanes.push(e0); lane = lanes.length - 1; } else lanes[lane] = e0;
    map.set(v.id, lane);
  });
  return { laneOf: (id: string) => map.get(id) || 0, count: Math.max(1, lanes.length) };
};
const rowHeight = (count: number) => count * LANE_H + 4;

interface ShiftCreationTimelineProps {
    currentStep: number;
    isClosed: boolean;
    positions: Position[];
    vacancies: Shift[];
    requests: Shift[];
    assignedShifts: Shift[];
    allStaff: Staff[];
    editDrag: { mode: 'move' | 'resize-start' | 'resize-end' | null; targetId: string | null; initialStartMinutes: number; initialEndMinutes: number; initialSlot: number; currentSlot: number };
    createDrag: { isDragging: boolean; positionId: string | null; startSlot: number | null; currentSlot: number | null };
    events: DailyEvent[];
    handleMouseDown: (positionId: string, slotIndex: number) => void;
    handleMouseEnter: (positionId: string, slotIndex: number) => void;
    handleMouseUp: () => void;
    handleEditDragStart: (e: React.MouseEvent, type: 'move'|'resize-start'|'resize-end', vacancy: Shift, clickedSlot: number) => void;
    setSelectedVacancyForCandidates: (vacancy: Shift) => void;
    setIsCandidateModalOpen: (isOpen: boolean) => void;
    openEditModal: (data: { vacancy: Shift; positionId: string }) => void;
    onDeleteVacancy?: (vacancy: Shift) => void; 
    onEventClick?: (dateStr: string, event?: DailyEvent) => void;
    currentDate: Date;
    getBorderColorClass: (posId: string | undefined, positions: Position[]) => string;
    baseStartTime?: string;
    baseEndTime?: string;
    isHoliday?: boolean;
    // 計画ステップでのポジション追加・削除
    onAddPosition?: () => void;
    onRemovePosition?: (position: Position) => void;
    // 対応業務のマーク表示（毎日必要な業務など）
    markDuties?: Duty[];
    applicantCount?: (v: Shift) => number;
    // 募集枠ごとの回答者数（希望・応募）
    respondentCount?: (v: Shift) => number;
    gapsOf?: (v: Shift) => CoverageGap[]; // 時間帯ごとの不足区間
}

// ポジションの色（淡い背景色）から、見分けやすい濃さの印の色を作る。
const dotColor = (color: string) => (color.split(' ')[0] || 'bg-slate-100').replace(/-(50|100|200)$/, '-400');

const ShiftCreationTimeline: React.FC<ShiftCreationTimelineProps> = ({
    currentStep, isClosed, positions, vacancies, requests, assignedShifts, allStaff,
    editDrag, createDrag, events, handleMouseDown, handleMouseEnter, handleMouseUp, handleEditDragStart,
    setSelectedVacancyForCandidates, setIsCandidateModalOpen, openEditModal, onDeleteVacancy, onEventClick, currentDate, getBorderColorClass,
    baseStartTime = '09:00',
    baseEndTime = '22:00',
    isHoliday = false,
    onAddPosition, onRemovePosition, markDuties = [], applicantCount = (v: Shift) => (v.applicants || []).length, respondentCount, gapsOf
}) => {
    const canEditPositions = currentStep === 0 && !isClosed;
    const dateStr = getJSTDateString(currentDate);
    const dayEvents = events.filter(e => {
        const start = e.date;
        const end = e.endDate || e.date;
        return dateStr >= start && dateStr <= end && e.title !== '休日'; 
    });

    // 表示する時間の範囲：店舗の基本時間に加えて、枠・希望・割り当てが範囲外（早朝・深夜営業）なら広げる
    const dayItems = [...vacancies, ...requests.filter(r => r.type !== 'off'), ...assignedShifts].filter(x => x.start.startsWith(dateStr));
    let startTotalMinutes = timeToMinutes(baseStartTime);
    let endTotalMinutes = timeToMinutes(baseEndTime);
    if (endTotalMinutes <= startTotalMinutes) {
        endTotalMinutes += 24 * 60;
    }
    if (dayItems.length > 0) {
        startTotalMinutes = Math.min(startTotalMinutes, Math.floor(Math.min(...dayItems.map(startMinOf)) / 60) * 60);
        endTotalMinutes = Math.max(endTotalMinutes, Math.ceil(Math.max(...dayItems.map(endMinOf)) / 60) * 60);
    }
    
    const displayRangeMinutes = endTotalMinutes - startTotalMinutes;
    const totalSlots = Math.ceil(displayRangeMinutes / 30);
    const SLOTS = Array.from({length: totalSlots}, (_, i) => i);

    const getPosStyle = (start: string, end: string) => {
        const sMin = startMinOf({ start }); const eMin = endMinOf({ start, end });

        const left = ((sMin - startTotalMinutes) / displayRangeMinutes) * 100;
        const width = ((eMin - sMin) / displayRangeMinutes) * 100;
        
        return { 
            left: `${Math.max(-100, Math.min(200, left))}%`, 
            width: `${Math.max(0, width)}%`,
            display: (left + width < 0 || left > 100) ? 'none' : 'flex'
        };
    };

    const getPreviewStyle = () => {
        if (!editDrag.mode || !editDrag.targetId) return {};
        const diffSlots = editDrag.currentSlot - editDrag.initialSlot; 
        const diffMinutes = diffSlots * 30;
        let start = editDrag.initialStartMinutes; 
        let end = editDrag.initialEndMinutes;
        
        if (editDrag.mode === 'move') { start += diffMinutes; end += diffMinutes; } 
        else if (editDrag.mode === 'resize-start') { start += diffMinutes; } 
        else if (editDrag.mode === 'resize-end') { end += diffMinutes; }
        
        const left = ((start - startTotalMinutes) / displayRangeMinutes) * 100;
        const width = ((end - start) / displayRangeMinutes) * 100;
        return { left: `${left}%`, width: `${width}%` };
    };

    const handleQuickAdjustHeadcount = async (e: React.MouseEvent, vacancy: Shift, delta: number) => {
        e.stopPropagation();
        const newValue = Math.max(1, Math.round((vacancy.headcount || 1) + delta));
        try {
            await api.saveShifts([{ ...vacancy, headcount: newValue }]);
        } catch (err) { 
            console.error("Quick adjust failed:", err);
        }
    };

    const timeLabels = [];
    for (let m = startTotalMinutes; m < endTotalMinutes; m += 60) { // 右端（終了時刻）のラベルははみ出すので出さない
        timeLabels.push({
            label: clockLabel(m), // 24時以降は「翌01:00」のように表示する
            percent: ((m - startTotalMinutes) / displayRangeMinutes) * 100
        });
    }

    return (
        <div className="flex flex-1 overflow-hidden relative select-none" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            {/* Sidebar */}
            <div className="w-48 lg:w-56 flex-shrink-0 border-r border-slate-100 bg-slate-50/50 z-[45]">
                <div className="h-10 border-b border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-400 tracking-wider uppercase bg-slate-100/50">ポジション</div>
                
                <div className="h-12 border-b border-slate-200 flex items-center px-4 lg:px-5 bg-amber-50/30">
                    <span className="font-bold text-[10px] text-amber-600 uppercase tracking-widest">イベント</span>
                </div>

                {positions.map(pos => {
                    const posVacancies = vacancies.filter(v => v.positionId === pos.id);
                    const totalReq = posVacancies.reduce((acc, v) => acc + (v.headcount || 1), 0);
                    const totalFilled = assignedShifts.filter(s => s.positionId === pos.id).length;
                    const gapSlots = posVacancies.filter(v => (gapsOf ? gapsOf(v) : vacancyGaps(v, vacancies, assignedShifts)).length > 0).length;
                    const isDeficit = gapSlots > 0;

                    return (
                        <div key={pos.id} className="border-b border-slate-100 flex flex-col justify-center px-4 lg:px-5 relative group/pos" style={{ height: rowHeight(laneLayout(posVacancies).count) }}>
                            <div className="flex items-center mb-1 min-w-0 pr-6">
                                <div className={`w-2 h-2 rounded-full mr-2 shrink-0 ${dotColor(pos.color)}`}></div>
                                <span className="font-bold text-[13px] text-slate-700 truncate">{pos.name}</span>
                            </div>
                            {canEditPositions && onRemovePosition && (
                                <button type="button" onClick={() => onRemovePosition(pos)} aria-label={`${pos.name}を削除`} title={`${pos.name}を削除します。`}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                                    <Trash2 size={14} />
                                </button>
                            )}
                            {currentStep >= 2 ? (
                                <div className={`text-[11px] font-bold tabular-nums ${totalReq === 0 ? 'text-slate-400' : isDeficit ? 'text-red-500' : 'text-green-600'}`}>
                                    {totalReq === 0 ? '枠なし' : isDeficit ? `不足のある枠 ${gapSlots}` : `充足（${totalFilled}名）`}
                                </div>
                            ) : (
                                <div className="text-[11px] font-bold tabular-nums text-slate-400">必要{totalReq}名</div>
                            )}
                        </div>
                    );
                })}
                {canEditPositions && onAddPosition && (
                    <div className="h-12 border-b border-slate-100 flex items-center px-3 lg:px-4">
                        <button type="button" onClick={onAddPosition} data-testid="add-position" className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-bold text-blue-600 border border-dashed border-blue-300 rounded hover:bg-blue-50 transition-colors whitespace-nowrap">
                            <Plus size={14} />ポジションを追加
                        </button>
                    </div>
                )}
            </div>

            {/* Timeline Area */}
            <div className="flex-1 overflow-x-auto relative">
                <div className="relative" style={{ minWidth: `${Math.max(720, totalSlots * 34)}px` }}>
                    <div className="h-10 border-b border-slate-200 bg-white sticky top-0 z-10 flex relative">
                        {timeLabels.map((tl, i) => (
                            <div 
                                key={i} 
                                className="absolute h-full border-r border-slate-50 text-[10px] lg:text-xs text-slate-400 font-medium flex items-center pl-2 whitespace-nowrap"
                                style={{ left: `${tl.percent}%` }}
                            >
                                {tl.label}
                            </div>
                        ))}
                    </div>
                    
                    <div className="relative">
                        <div className="absolute inset-0 flex pointer-events-none">
                            {SLOTS.map((slot) => (
                                <div key={slot} className={`flex-1 border-r ${slot % 2 === 1 ? 'border-slate-100' : 'border-slate-50 border-dashed'}`}></div>
                            ))}
                        </div>

                        {/* 店休日のUI刷新: ミニマル・構造的デザイン */}
                        {isHoliday && (
                            <div className="absolute inset-0 z-[60] bg-slate-50/60 flex items-center justify-center pointer-events-none">
                                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #64748b 0, #64748b 1px, transparent 0, transparent 10px)' }}></div>
                                <div className="bg-white px-8 py-3 rounded border border-slate-200 shadow-sm flex items-center gap-3">
                                    <Lock size={16} className="text-slate-400" />
                                    <span className="text-sm font-black text-slate-800 tracking-widest uppercase">店休日</span>
                                </div>
                            </div>
                        )}

                        <div className="h-12 border-b border-slate-200 relative bg-amber-50/10 cursor-pointer" onClick={() => !isHoliday && onEventClick?.(dateStr)}>
                            {dayEvents.map(ev => {
                                const style = ev.isAllDay ? { left: '0.5%', width: '99%', display: 'flex' } : getPosStyle(`${dateStr}T${ev.startTime || '00:00'}:00`, `${dateStr}T${ev.endTime || '23:59'}:00`);
                                return (
                                    <div key={ev.id} onClick={(e) => { e.stopPropagation(); !isHoliday && onEventClick?.(dateStr, ev); }} className="absolute top-1.5 bottom-1.5 border border-amber-300 rounded z-20 flex items-center px-3 bg-amber-100 text-amber-900 shadow-sm" style={style as React.CSSProperties}>
                                        <span className="text-[10px] font-black truncate">{ev.title}</span>
                                    </div>
                                );
                            })}
                        </div>

                        {positions.map(pos => {
                            const posVacancies = vacancies.filter(v => v.positionId === pos.id);
                            const layout = laneLayout(posVacancies);
                            const allocation = allocateToVacancies(posVacancies, assignedShifts.filter(a => a.positionId === pos.id));
                            return (
                                <div key={pos.id} className="border-b border-slate-100 relative group transition-colors hover:bg-slate-50/30" style={{ height: rowHeight(layout.count) }}>
                                    {!isHoliday && (
                                        <div className="absolute inset-0 flex z-[10]">
                                            {SLOTS.map((slot) => (
                                                <div 
                                                  key={slot} 
                                                  className="flex-1 cursor-pointer" 
                                                  onMouseDown={() => handleMouseDown(pos.id, slot + (startTotalMinutes / 30))} 
                                                  onMouseEnter={() => handleMouseEnter(pos.id, slot + (startTotalMinutes / 30))} 
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {posVacancies.map(vacancy => {
                                        const vsMin = startMinOf(vacancy); const veMin = endMinOf(vacancy);
                                        // この枠の人として表示する割り当て（重なる枠が複数ある場合は振り分ける）
                                        const assignedToThis = (allocation.get(vacancy.id) || []).filter(as => startMinOf(as) < veMin && endMinOf(as) > vsMin);
                                        const gaps = gapsOf ? gapsOf(vacancy) : vacancyGaps(vacancy, vacancies, assignedShifts);
                                        const isFull = gaps.length === 0;
                                        const assignedStaff = assignedToThis.map(s => allStaff.find(x => x.id === s.staffId) || null);
                                        const relevantRequests = requests.filter(r => r.start === vacancy.start && r.end === vacancy.end);
                                        const isBeingEdited = editDrag.targetId === vacancy.id;
                                        const startTime = clockLabel(vsMin);
                                        const endTime = clockLabel(veMin);
                                        
                                        let statusBg = 'bg-white/95';
                                        if (currentStep >= 2) statusBg = isFull ? 'bg-green-50/90 border-green-300' : 'bg-red-50/90 border-red-300 text-red-600';

                                        const sMin = vsMin;
                                        const duration = veMin - vsMin;
                                        const isNarrow = duration <= 60;

                                        return (
                                            <div 
                                                key={vacancy.id} 
                                                className={`absolute border-2 rounded-[4px] z-[20] flex flex-col justify-center px-1 shadow-sm transition-all group/item cursor-pointer overflow-visible ${currentStep === 0 ? 'bg-white border-blue-500' : ''} ${currentStep === 1 ? 'bg-gray-50 border-gray-200 text-gray-500' : ''} ${currentStep >= 2 ? statusBg : ''} ${isBeingEdited ? 'opacity-30 pointer-events-none' : ''}`} 
                                                style={{ ...getPosStyle(vacancy.start, vacancy.end), top: 4 + layout.laneOf(vacancy.id) * LANE_H, height: LANE_H - 4 }} 
                                            >
                                                {currentStep >= 1 && (
                                                    <button type="button" className="absolute inset-0 z-[35] cursor-pointer" title={currentStep === 1 ? "回答したスタッフを表示" : "候補を表示"} onClick={(e) => { e.stopPropagation(); setSelectedVacancyForCandidates(vacancy); setIsCandidateModalOpen(true); }} aria-label="候補を表示" />
                                                )}
                                                <div className="absolute inset-0 flex z-[30]">
                                                    {Array.from({ length: Math.max(1, Math.ceil(duration / 30)) }).map((_, i) => {
                                                        const slotIdx = Math.floor(sMin / 30) + i;
                                                        return (
                                                            <div 
                                                                key={i} 
                                                                className="flex-1 cursor-pointer"
                                                                onMouseDown={(e) => handleEditDragStart(e, 'move', vacancy, slotIdx)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                
                                                {currentStep === 0 && !isHoliday && (
                                                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center bg-white shadow-xl border border-slate-200 rounded-full px-1 py-0.5 opacity-0 group-hover/item:opacity-100 group-hover/item:-bottom-5 transition-all duration-200 z-[50] pointer-events-auto">
                                                        <button 
                                                            onClick={(e) => handleQuickAdjustHeadcount(e, vacancy, -1)} 
                                                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-full transition-colors"
                                                            title="減らす"
                                                        >
                                                            <Minus size={10} strokeWidth={3} />
                                                        </button>
                                                        <span className="px-1.5 text-[10px] font-black text-slate-800 min-w-[20px] text-center whitespace-nowrap">
                                                            {vacancy.headcount || 1}名
                                                        </span>
                                                        <button 
                                                            onClick={(e) => handleQuickAdjustHeadcount(e, vacancy, 1)} 
                                                            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-slate-100 rounded-full transition-colors"
                                                            title="増やす"
                                                        >
                                                            <Plus size={10} strokeWidth={3} />
                                                        </button>
                                                    </div>
                                                )}

                                                {currentStep === 0 && !isHoliday && onDeleteVacancy && (
                                                    <button onClick={(e) => { e.stopPropagation(); onDeleteVacancy(vacancy); }} className="absolute -top-2 -right-2 p-1 text-white bg-slate-800 hover:bg-red-500 rounded-full z-[40] opacity-0 group-hover/item:opacity-100 transition-all shadow-md"><Trash2 size={10} /></button>
                                                )}

                                                {currentStep === 0 && !isBeingEdited && !isHoliday && (
                                                    <>
                                                        <div 
                                                          onMouseDown={(e) => { 
                                                              const slot = Math.floor(sMin / 30);
                                                              handleEditDragStart(e, 'resize-start', vacancy, slot); 
                                                          }} 
                                                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-[40] hover:bg-blue-500/20" 
                                                        />
                                                        <div 
                                                          onMouseDown={(e) => { 
                                                              const slot = Math.floor((sMin + duration) / 30) - 1;
                                                              handleEditDragStart(e, 'resize-end', vacancy, slot); 
                                                          }} 
                                                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-[40] hover:bg-blue-500/20" 
                                                        />
                                                    </>
                                                )}
                                                
                                                {/* 人数が足りない時間帯を、枠の下端に赤い帯で示す */}
                                                {currentStep >= 2 && gaps.map(g => (
                                                    <div key={g.start} className="absolute bottom-0 h-1.5 bg-red-500/80 rounded-sm z-[5] pointer-events-none" title={`${gapsLabel([g])}不足`}
                                                        style={{ left: `${((g.start - sMin) / duration) * 100}%`, width: `${((g.end - g.start) / duration) * 100}%` }} />
                                                ))}
                                                <div className="relative z-0 pointer-events-none flex flex-col items-center justify-center gap-0.5 w-full h-full pb-1.5">
                                                    <div className={`${isNarrow ? 'text-[10px]' : 'text-[13px]'} font-bold text-slate-800 whitespace-nowrap overflow-hidden tabular-nums`}>
                                                        {startTime}〜{endTime}
                                                    </div>
                                                    
                                                    {currentStep >= 2 && (
                                                        <div className="flex items-center gap-1 max-w-full px-1 leading-tight">
                                                            <span className={`text-[11px] font-bold tabular-nums whitespace-nowrap truncate ${isFull ? 'text-green-700' : 'text-red-600'}`}>{isFull ? '充足' : isNarrow ? '不足' : `${gapsLabel(gaps)}不足`}</span>
                                                            {!isNarrow && !isFull && vacancy.openCall && <span className="text-[11px] font-bold text-orange-600 border border-orange-200 bg-orange-50 rounded px-1 whitespace-nowrap shrink-0">募集中{applicantCount(vacancy) > 0 ? `・応募${applicantCount(vacancy)}` : ''}</span>}
                                                        </div>
                                                    )}
                                                    {currentStep >= 2 && !isNarrow && assignedStaff.length > 0 && (
                                                        // 割り当てた人（枠の一部の時間だけ入る人は時間を添える。例：池田 11-13）。入りきらない場合は末尾を省略する
                                                        <div className="text-[11px] font-bold text-slate-600 max-w-full px-1.5 truncate leading-tight" title={assignedStaff.map(st => st ? st.lastName : '未定').join('・')}>
                                                            {assignedStaff.map((st, i) => {
                                                                const sh = assignedToThis[i]; const a0 = startMinOf(sh); const a1 = endMinOf(sh);
                                                                const partial = a0 !== vsMin || a1 !== veMin;
                                                                return (
                                                                    <React.Fragment key={i}>{i > 0 && <span className="text-slate-300">・</span>}{st ? st.lastName : '未定'}<DutyMarks staff={st} duties={markDuties} size="xs" />{partial && <span className="text-slate-400 font-medium tabular-nums">{[a0, a1].map(t => `${Math.floor(t / 60) % 24}${t % 60 ? `:${String(t % 60).padStart(2, '0')}` : ''}`).join('-')}</span>}</React.Fragment>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {currentStep === 1 && !isNarrow && <div className="text-[11px] font-bold mt-0.5 text-slate-500 whitespace-nowrap">{respondentCount ? respondentCount(vacancy) : relevantRequests.length}名回答</div>}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {editDrag.mode && editDrag.targetId && vacancies.some(v => v.id === editDrag.targetId && v.positionId === pos.id) && (
                                        <div className="absolute top-1 bottom-1 bg-blue-600/30 border-2 border-blue-600 border-dashed rounded z-[30] pointer-events-none" style={getPreviewStyle()} />
                                    )}

                                    {createDrag.isDragging && createDrag.positionId === pos.id && createDrag.startSlot !== null && createDrag.currentSlot !== null && (
                                        <div 
                                            className="absolute top-1 bottom-1 bg-blue-500/20 border-2 border-blue-500 border-dashed rounded z-[30] pointer-events-none" 
                                            style={{
                                                left: `${((Math.min(createDrag.startSlot, createDrag.currentSlot) * 30 - startTotalMinutes) / displayRangeMinutes) * 100}%`, 
                                                width: `${((Math.abs(createDrag.currentSlot - createDrag.startSlot) + 1) * 30 / displayRangeMinutes) * 100}%`
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                        {canEditPositions && onAddPosition && <div className="h-12 border-b border-slate-100" />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShiftCreationTimeline;
